/**
 * VerifyIdentity - Document verification page for drivers and merchants
 * Upload ID documents, business registration, and selfie for platform verification
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  ArrowLeft, 
  Shield, 
  Upload, 
  FileCheck, 
  Camera, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Info,
  Loader2,
  Trash2
} from 'lucide-react';

// ID Types for selection
const ID_TYPES = {
  driver: [
    { value: 'national_id', label: 'National ID (TRN)' },
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'passport', label: 'Passport' }
  ],
  merchant: [
    { value: 'business_registration', label: 'Business Registration' },
    { value: 'tax_document', label: 'Tax Document (TRN)' },
    { value: 'national_id', label: 'National ID (Owner)' }
  ]
};

// Verification status display
const STATUS_CONFIG = {
  verified: { bg: 'bg-green-500/10', text: 'text-green-400', icon: CheckCircle, label: 'Verified' },
  pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: Clock, label: 'Pending Review' },
  under_review: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: Clock, label: 'Under Review' },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle, label: 'Rejected' },
  not_submitted: { bg: 'bg-slate-500/10', text: 'text-slate-400', icon: Shield, label: 'Not Verified' }
};

export default function VerifyIdentity() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const roleParam = searchParams.get('role') || 'driver';
  const [role, setRole] = useState(roleParam);
  
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [documentFiles, setDocumentFiles] = useState([]);
  const [selfieFile, setSelfieFile] = useState(null);
  const [uploadedPaths, setUploadedPaths] = useState([]);
  const [selfiePath, setSelfiePath] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const res = await api.get(`/security/verify/status?role=${role}`);
      setVerificationStatus(res.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  const handleFileSelect = useCallback((e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'selfie') {
      setSelfieFile(files[0]);
    } else {
      setDocumentFiles(prev => [...prev, ...files].slice(0, 5));
    }
  }, []);

  const removeFile = (index) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedPaths(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    setUploading(true);
    const paths = [];
    
    try {
      // Upload document files
      for (const file of documentFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post(`/security/verify/upload-document?doc_type=${idType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        paths.push(res.data.file_path);
      }
      setUploadedPaths(paths);
      
      // Upload selfie if provided
      if (selfieFile) {
        const formData = new FormData();
        formData.append('file', selfieFile);
        const res = await api.post('/security/verify/upload-document?doc_type=selfie', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSelfiePath(res.data.file_path);
      }
      
      toast.success('Documents uploaded successfully');
      return { paths, selfiePath: selfieFile ? selfiePath : null };
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload documents');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!idType || !idNumber) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (documentFiles.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // First upload files
      let paths = uploadedPaths;
      let selfie = selfiePath;
      
      if (uploadedPaths.length === 0) {
        const uploadResult = await uploadFiles();
        if (!uploadResult) {
          setSubmitting(false);
          return;
        }
        paths = uploadResult.paths;
        selfie = uploadResult.selfiePath;
      }
      
      // Submit verification request
      await api.post(`/security/verify/submit?role=${role}`, {
        id_type: idType,
        id_number: idNumber,
        document_paths: paths,
        selfie_path: selfie
      });
      
      toast.success('Verification submitted! We will review your documents.');
      fetchVerificationStatus();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = STATUS_CONFIG[verificationStatus?.status || 'not_submitted'];
  const StatusIcon = statusConfig.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading verification status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-500" />
                Identity Verification
              </h1>
              <p className="text-sm text-slate-400">
                Verify your identity to unlock all features
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Role Selector */}
        <div className="flex gap-2">
          <Button
            variant={role === 'driver' ? 'default' : 'outline'}
            onClick={() => setRole('driver')}
            className={role === 'driver' ? 'bg-green-600' : 'border-slate-700'}
          >
            Driver Verification
          </Button>
          <Button
            variant={role === 'merchant' ? 'default' : 'outline'}
            onClick={() => setRole('merchant')}
            className={role === 'merchant' ? 'bg-green-600' : 'border-slate-700'}
          >
            Merchant Verification
          </Button>
        </div>

        {/* Current Status Card */}
        <Card className={`p-6 ${statusConfig.bg} border-slate-700`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full ${statusConfig.bg} flex items-center justify-center`}>
                <StatusIcon className={`w-6 h-6 ${statusConfig.text}`} />
              </div>
              <div>
                <h3 className="font-semibold text-white">{role === 'driver' ? 'Driver' : 'Merchant'} Verification Status</h3>
                <Badge className={`mt-1 ${statusConfig.bg} ${statusConfig.text}`}>
                  {statusConfig.label}
                </Badge>
              </div>
            </div>
            
            {verificationStatus?.verified && (
              <CheckCircle className="w-8 h-8 text-green-400" />
            )}
          </div>
          
          {verificationStatus?.status === 'rejected' && verificationStatus?.rejection_reason && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">
                <strong>Rejection Reason:</strong> {verificationStatus.rejection_reason}
              </p>
            </div>
          )}
          
          {verificationStatus?.reviewed_at && (
            <p className="mt-2 text-sm text-slate-400">
              Reviewed: {new Date(verificationStatus.reviewed_at).toLocaleDateString()}
            </p>
          )}
        </Card>

        {/* Benefits Card */}
        <Card className="p-6 bg-slate-800/50 border-slate-700">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            Why Verify?
          </h3>
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Higher trust score and priority for deliveries
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Access to premium incentives and bonuses
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Faster payout processing
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Verified badge on your profile
            </li>
          </ul>
        </Card>

        {/* Verification Form */}
        {(!verificationStatus?.verified && verificationStatus?.status !== 'pending') && (
          <Card className="p-6 bg-slate-800/50 border-slate-700">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-green-400" />
              Submit Verification
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* ID Type Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Document Type *
                </label>
                <select
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select document type...</option>
                  {ID_TYPES[role].map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* ID Number */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {role === 'merchant' ? 'Registration/Tax Number' : 'ID Number'} *
                </label>
                <Input
                  type="text"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  placeholder="Enter your document number"
                  className="bg-slate-900 border-slate-700"
                  required
                />
              </div>

              {/* Document Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Document Photos * (Max 5)
                </label>
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => handleFileSelect(e, 'document')}
                    className="hidden"
                    id="document-upload"
                  />
                  <label htmlFor="document-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      Click to upload or drag & drop
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      JPG, PNG, WebP, or PDF (Max 5 files)
                    </p>
                  </label>
                </div>
                
                {/* Uploaded Files Preview */}
                {documentFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {documentFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileCheck className="w-5 h-5 text-green-400" />
                          <span className="text-sm text-slate-300">{file.name}</span>
                          <span className="text-xs text-slate-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selfie Upload (Optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Selfie with ID (Recommended)
                </label>
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handleFileSelect(e, 'selfie')}
                    className="hidden"
                    id="selfie-upload"
                  />
                  <label htmlFor="selfie-upload" className="cursor-pointer">
                    <Camera className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      Take or upload a selfie holding your ID
                    </p>
                  </label>
                </div>
                
                {selfieFile && (
                  <div className="mt-4 flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Camera className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-slate-300">{selfieFile.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelfieFile(null)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Warning */}
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div className="text-sm text-yellow-400">
                    <p className="font-medium">Important:</p>
                    <ul className="mt-1 space-y-1 text-yellow-400/80">
                      <li>• Ensure all documents are clear and readable</li>
                      <li>• Information must match your  account</li>
                      <li>• False information may result in account suspension</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || uploading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {submitting || uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploading ? 'Uploading...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </form>
          </Card>
        )}

        {/* Pending Status Info */}
        {verificationStatus?.status === 'pending' && (
          <Card className="p-6 bg-yellow-500/10 border-yellow-500/30">
            <div className="flex items-start gap-4">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div>
                <h3 className="font-semibold text-yellow-400">Verification In Progress</h3>
                <p className="text-sm text-slate-300 mt-1">
                  Your documents are being reviewed. This typically takes 1-2 business days.
                  We will notify you once the review is complete.
                </p>
                {verificationStatus?.submitted_at && (
                  <p className="text-xs text-slate-400 mt-2">
                    Submitted: {new Date(verificationStatus.submitted_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
